import React from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay';
import get from 'lodash.get';

// components
import { PdpSharedBlurbPcs } from '../PdpSharedBlurbs/PdpSharedBlurbPcs';
import { PdpSharedBlurbLeadTime } from '../PdpSharedBlurbs/PdpSharedBlurbLeadTime';
import { PdpSharedBlurbShipping } from '../PdpSharedBlurbs/PdpSharedBlurbShipping';
import { PdpSharedBlurbReturns } from '../PdpSharedBlurbs/PdpSharedBlurbReturns';
import { PdpSharedBlurbUrgency } from '../PdpSharedBlurbs/PdpSharedBlurbUrgency';
import { PdpSharedBlurbSupportNumber } from '../PdpSharedBlurbs/PdpSharedBlurbSupportNumber';

// helpers
import classnames from 'classnames';

// styles
import styles from './PdpRespSidebarBlurbs.scss';

export function PdpRespSidebarBlurbsComponent(props) {
    const {
        currency,
        relayVariables,
        viewer,
        item,
        user,
        displayInfo,
        vipUserRelationship,
        shipmentQuote,
        shipment,
        showLeadTimeBlurb,
        isPdpFreeShippingTestVariant,
    } = props;
    const pricingDisplayInfo = get(displayInfo, 'pricing') || null;
    const hasValidPricingDisplayInfo = !!pricingDisplayInfo && !pricingDisplayInfo.showUnavailable;
    const renderBlurbs = hasValidPricingDisplayInfo && !pricingDisplayInfo.showSold;
    const renderSoldPdpBlurbs = hasValidPricingDisplayInfo && pricingDisplayInfo.showSold;

    if (renderSoldPdpBlurbs) {
        return (
            <PdpSharedBlurbPcs
                user={user}
                vipUserRelationship={vipUserRelationship}
                item={item}
                isSoldPdp
            />
        );
    } else if (!renderBlurbs) {
        return null;
    }

    return (
        <div
            className={classnames(styles.container, {
                [styles.borderTopHide]: isPdpFreeShippingTestVariant,
            })}
        >
            {!relayVariables.isTrade && !isPdpFreeShippingTestVariant && (
                <PdpSharedBlurbUrgency item={item} />
            )}
            <PdpSharedBlurbPcs user={user} vipUserRelationship={vipUserRelationship} item={item} />
            {showLeadTimeBlurb && <PdpSharedBlurbLeadTime item={item} />}
            <PdpSharedBlurbShipping
                currency={currency}
                itemId={relayVariables.itemId}
                item={item}
                userId={relayVariables.userId}
                viewer={viewer}
                user={user}
                shipmentQuote={shipmentQuote}
                shipment={shipment}
                renderTooltip
                isPdpFreeShippingTestVariant={isPdpFreeShippingTestVariant}
            />
            {!isPdpFreeShippingTestVariant && (
                <PdpSharedBlurbReturns
                    item={item}
                    itemId={relayVariables.itemId}
                    user={user}
                    userId={relayVariables.userId}
                />
            )}
            <PdpSharedBlurbSupportNumber viewer={viewer} user={user} item={item} />
        </div>
    );
}

PdpRespSidebarBlurbsComponent.defaultProps = {
    showLeadTimeBlurb: true,
};

PdpRespSidebarBlurbsComponent.propTypes = {
    currency: PropTypes.string,
    relayVariables: PropTypes.shape({
        isTrade: PropTypes.bool,
        isVip: PropTypes.bool,
        userId: PropTypes.string,
        itemId: PropTypes.string.isRequired,
        isClient: PropTypes.bool,
    }).isRequired,
    viewer: PropTypes.object,
    item: PropTypes.object.isRequired,
    user: PropTypes.object,
    displayInfo: PropTypes.object,
    vipUserRelationship: PropTypes.object,
    shipmentQuote: PropTypes.object,
    shipment: PropTypes.object,
    showLeadTimeBlurb: PropTypes.bool,
    isPdpFreeShippingTestVariant: PropTypes.bool,
};

export const PdpRespSidebarBlurbs = createFragmentContainer(PdpRespSidebarBlurbsComponent, {
    viewer: graphql`
        fragment PdpRespSidebarBlurbs_viewer on Viewer {
            ...PdpSharedBlurbShipping_viewer
            ...PdpSharedBlurbSupportNumber_viewer
        }
    `,
    item: graphql`
        fragment PdpRespSidebarBlurbs_item on Item {
            ...PdpSharedBlurbLeadTime_item
            ...PdpSharedBlurbReturns_item
            ...PdpSharedBlurbShipping_item
            ...PdpSharedBlurbUrgency_item
            ...PdpSharedBlurbSupportNumber_item
            ...PdpSharedBlurbPcs_item
        }
    `,
    user: graphql`
        fragment PdpRespSidebarBlurbs_user on User {
            ...PdpSharedBlurbPcs_user
            ...PdpSharedBlurbShipping_user
            ...PdpSharedBlurbReturns_user
            ...PdpSharedBlurbShipping_user
            ...PdpSharedBlurbSupportNumber_user
        }
    `,
    displayInfo: graphql`
        fragment PdpRespSidebarBlurbs_displayInfo on ItemDisplayInfo {
            pricing {
                showSold
                showUnavailable
            }
        }
    `,
    vipUserRelationship: graphql`
        fragment PdpRespSidebarBlurbs_vipUserRelationship on UserRelationship {
            ...PdpSharedBlurbPcs_vipUserRelationship
        }
    `,
    shipmentQuote: graphql`
        fragment PdpRespSidebarBlurbs_shipmentQuote on ShipmentQuote {
            ...PdpSharedBlurbShipping_shipmentQuote
        }
    `,
    shipment: graphql`
        fragment PdpRespSidebarBlurbs_shipment on Shipment {
            ...PdpSharedBlurbShipping_shipment
        }
    `,
});
